/**
 * investmentCalculations.js
 *
 * Pure investment calculation functions for the campaign editor.
 * No React, no Redux, no side effects — all functions are (inputs) → outputs.
 *
 * Single source of truth for the spots ↔ investment formula and all
 * budget-distribution logic used across the campaign editor.
 *
 * Exports:
 *   spots       — primitive conversions between impressions and money
 *   budget      — budget constraints, fixed amounts and validation
 *   distribution — algorithms that spread investment across displays
 */

import { getDiscountFloorSpots } from '../../../../services/display';

// ---------------------------------------------------------------------------
// spots — primitive conversions
// ---------------------------------------------------------------------------

/**
 * Calculate spots (impressions) from a budget amount and CPM.
 * @param {number} investment - Budget amount
 * @param {number} cpm        - Cost per thousand impressions (full CPM, e.g. 5000)
 * @returns {number}          - Number of spots (round integer)
 */
function spotsFromBudget(investment, cpm) {
  if (!cpm || cpm <= 0) return 0;
  return Math.round(investment / (cpm / 1000));
}

/**
 * Calculate investment (budget) from spots and CPM.
 * @param {number} spots - Number of impressions
 * @param {number} cpm   - Cost per thousand impressions (full CPM)
 * @returns {number}     - Budget amount
 */
function budgetFromSpots(spots, cpm) {
  return spots * (cpm / 1000);
}

/**
 * Calculate spots from budget, carrying the fractional remainder as a budget
 * value for the next item in a chained distribution.
 * @param {number} investment - Budget amount (may include previous remainder)
 * @param {number} cpm        - Full CPM
 * @returns {{ spots: number, remainder: number }}
 */
function spotsFromBudgetWithRemainder(investment, cpm) {
  if (!cpm || cpm <= 0) return { spots: 0, remainder: investment };
  const spotPrice = cpm / 1000;
  const raw = investment / spotPrice;
  const spots = Math.trunc(raw);
  const remainder = (raw % 1) * spotPrice;
  return { spots, remainder };
}

/**
 * playouts → impressions.
 * @param {number} promisedShows - promised_shows
 * @param {number} multiplier      - de getMultiplyByStatus
 * @returns {number}
 */
function impressionsFromSpots(promisedShows, multiplier) {
  return Math.round((promisedShows || 0) * multiplier);
}

/**
 * Impressions → playouts.
 * @param {number} impressions
 * @param {number} multiplier - de getMultiplyByStatus
 * @returns {number} promised_shows
 */
function spotsFromImpressions(impressions, multiplier) {
  return Math.round((impressions || 0) / multiplier);
}

/**
 * Presupuesto → impresiones. Espejo de spotsFromBudget.
 * @param {number} investment
 * @param {number} cpmi
 * @returns {number}
 */
function impressionsFromBudget(investment, cpmi) {
  if (!cpmi || cpmi <= 0) return 0;
  return Math.round(investment / (cpmi / 1000));
}

/**
 * Impresiones → presupuesto. Sin redondeo, igual que budgetFromSpots.
 * @param {number} impressions
 * @param {number} cpmi - CPMi completo
 * @returns {number}
 */
function budgetFromImpressions(impressions, cpmi) {
  return (impressions || 0) * (cpmi / 1000);
}

/**
 * CPM efectivo: reparte lo que se cobra entre TODAS las impresiones entregadas,
 * incluyendo las bonificadas, que no se cobran.
 *
 * `baseImpressions` son las que el `cpmi` base ya cubre: compradas más el bonus
 * por volumen, porque ese descuento no baja la tarifa, regala salidas.
 *
 * @param {number} cpmi                   - CPMi base (con descuento por volumen si aplica)
 * @param {number} baseImpressions        - impresiones que ese CPMi ya cubre
 * @param {number} subsidizedImpressions  - impresiones bonificadas (gratis)
 * @returns {number}
 */
function effectiveCpmi(cpmi, baseImpressions, subsidizedImpressions) {
  const base = baseImpressions || 0;
  const subsidized = subsidizedImpressions || 0;
  // With no subsidized delivery the rate is untouched. Returning it verbatim
  // avoids the (a*b)/b float drift that left the result ~1e-15 below the base
  // rate and made the UI strike through a price identical to the effective one.
  if (subsidized <= 0 || base + subsidized <= 0) return cpmi || 0;
  return ((cpmi || 0) * base) / (base + subsidized);
}

/**
 *
 * @param {Object} display
 * @returns {number|null}
 */
function getMultiplyFromPrices(display) {
  if (!display) return null;
  const cpm = Number(display.smart_campaign_cpm);
  const cpmi = Number(display.cpmi);
  if (!isFinite(cpm) || !isFinite(cpmi) || cpm <= 0 || cpmi <= 0) return null;
  return cpm / cpmi;
}

// ---------------------------------------------------------------------------
// budget — constraints and validation
// ---------------------------------------------------------------------------

/**
 * Sum the budget assigned to all fixed-budget items in a customContents array.
 * @param {Array}    items           - customContents array
 * @param {object}   options
 * @param {function} options.getCpm  - (item) => full CPM for that item
 * @param {function} options.isFixed - (item) => boolean
 * @returns {number}
 */
function sumFixed(items, { getCpm, isFixed }) {
  return items.reduce((total, item) => {
    if (!isFixed(item)) return total;
    return total + budgetFromSpots(item.promised_shows, getCpm(item));
  }, 0);
}

/**
 * Minimum budget a group must keep (sum of all its fixed-budget contents).
 * @param {Array}    groupDisplays   - Displays in the group
 * @param {Array}    customContents
 * @param {object}   [options]
 * @param {function} [options.getCpm] - (display) => full CPM; defaults to display.smart_campaign_cpm
 * @returns {number}
 */
function calcGroupMin(groupDisplays, customContents, { getCpm } = {}) {
  const resolveCpm = getCpm || (display => display.smart_campaign_cpm);
  return groupDisplays.reduce((total, display) => {
    const fixedContents = customContents.filter(c => c.displayId === display.id && c.isFixedBudget);
    const cpm = resolveCpm(display);
    return total + fixedContents.reduce((sum, content) => sum + budgetFromSpots(content.promised_shows, cpm), 0);
  }, 0);
}

/**
 * Maximum budget available for a group (parent budget minus siblings' fixed budgets).
 * @param {number}   parentBudget    - Parent group's total budget
 * @param {Array}    siblingDisplays - Displays in sibling groups
 * @param {Array}    customContents
 * @param {object}   [options]
 * @param {function} [options.getCpm] - (display) => full CPM
 * @returns {number}
 */
function calcGroupMax(parentBudget, siblingDisplays, customContents, { getCpm } = {}) {
  const resolveCpm = getCpm || (display => display.smart_campaign_cpm);
  const fixedSiblingBudget = siblingDisplays.reduce((total, display) => {
    const fixedContents = customContents.filter(c => c.displayId === display.id && c.isFixedBudget);
    const cpm = resolveCpm(display);
    return total + fixedContents.reduce((sum, content) => sum + budgetFromSpots(content.promised_shows, cpm), 0);
  }, 0);
  return parentBudget - fixedSiblingBudget;
}

/**
 * Normalize a budget amount: clamp infinities and negatives.
 * @param {number} amount
 * @returns {number} - Value in [0, 999999999999]
 */
function normalize(amount) {
  let result = amount > 0 && amount !== Infinity ? amount : 0;
  if (result === Infinity || result > 999999999999) result = 999999999999;
  return result;
}

// ---------------------------------------------------------------------------
// distribution — algorithms that spread investment across displays
// ---------------------------------------------------------------------------

/**
 * Distribute a total budget evenly across non-fixed customContents, respecting
 * the display order in `displays` and carrying fractional remainders between items.
 *
 * Core of `updateByAmountBudget` in CampaignManagerComponent.
 *
 * @param {number}   totalBudget - Total budget to distribute
 * @param {Array}    displays    - Ordered display objects (iteration order + CPM source)
 * @param {Array}    items       - customContents array
 * @param {object}   options
 * @param {function} options.getCpm   - (display) => full CPM
 * @param {function} options.isFixed  - (item) => boolean
 * @returns {Array} - Updated customContents with recalculated promised_shows
 */
function byBudget(totalBudget, displays, items, { getCpm, isFixed }) {
  const fixedBudget = items.reduce((total, item) => {
    if (!isFixed(item)) return total;
    const display = displays.find(d => d.id === item.displayId);
    if (!display) return total;
    return total + budgetFromSpots(item.promised_shows, getCpm(display));
  }, 0);

  const availableBudget = parseFloat(totalBudget).toFixed(2) - fixedBudget;

  const uniqueNonFixedDisplayIds = [...new Set(
    items.filter(item => !isFixed(item)).map(item => item.displayId),
  )];

  if (uniqueNonFixedDisplayIds.length === 0) return items;

  const budgetPerDisplay = availableBudget / uniqueNonFixedDisplayIds.length;

  const updatedItems = [...items];
  let remainderCampaign = 0;

  displays.forEach(display => {
    const nonFixedContents = items.filter(c => c.displayId === display.id && !isFixed(c));
    if (nonFixedContents.length === 0) return;

    const spotPrice = getCpm(display) / 1000;
    let remainderDisplay = remainderCampaign;

    nonFixedContents.forEach(content => {
      const amountForRelation = (budgetPerDisplay / nonFixedContents.length) + remainderDisplay;
      const rawShows = spotPrice > 0 ? amountForRelation / spotPrice : 0;
      const spots = Math.trunc(rawShows);
      remainderDisplay = (rawShows % 1) * spotPrice;

      const idx = updatedItems.findIndex(c => c.relationId === content.relationId);
      if (idx !== -1) {
        updatedItems[idx] = { ...updatedItems[idx], promised_shows: spots || 0 };
      }
    });

    remainderCampaign = remainderDisplay || 0;
  });

  return updatedItems;
}

/**
 * Distribute budget so every content gets the same number of spots (Balance).
 *
 * Core of `redistributeImpressionsEquitably` in ScreensOutputSettingsComponent.
 *
 * @param {number}   totalBudget - Total budget
 * @param {Array}    items       - customContents array
 * @param {object}   options
 * @param {function} options.getCpm - (item) => full CPM for that item
 * @returns {Array} - Updated customContents with equal promised_shows
 */
function equitably(totalBudget, items, { getCpm }) {
  const totalCostPerImpression = items.reduce((acc, item) => {
    return acc + (getCpm(item) / 1000);
  }, 0);

  if (totalCostPerImpression === 0) return items;

  const totalImpressions = Math.floor(totalBudget / totalCostPerImpression);
  let assignedBudget = 0;

  let result = items.map(item => {
    assignedBudget += totalImpressions;
    return { ...item, promised_shows: totalImpressions };
  });

  let remainingBudget = totalBudget - assignedBudget;

  for (let i = 0; i < result.length && remainingBudget > 0; i++) {
    const extraCost = getCpm(result[i]);
    if (remainingBudget >= extraCost) {
      result[i] = { ...result[i], promised_shows: result[i].promised_shows + 1 };
      remainingBudget -= extraCost;
    }
  }

  return result;
}

/**
 * Redistribute investment when a group's budget changes.
 *
 * Pure extraction of `handleChangeGroupProposal` in ScreensCollectionComponent.
 * Handles both 'default' (absolute amount) and 'percentage' (relative slider) modes.
 *
 * @param {number}   newGroupBudget  - New budget for the group being changed
 * @param {Array}    groupDisplays   - Displays in the group being changed
 * @param {Array}    customContents  - Full customContents array
 * @param {Array}    siblingDisplays - Displays in all other groups (same level)
 * @param {number}   parentBudget    - Total budget of the parent level
 * @param {object}   [options]
 * @param {function} [options.getCpm]       - (display) => full CPM; defaults to display.smart_campaign_cpm
 * @param {string}   [options.typeOfUpdate] - 'default' | 'percentage'
 * @returns {Array} - Updated customContents (no side effects)
 */
function byGroup(
  newGroupBudget,
  groupDisplays,
  customContents,
  siblingDisplays,
  parentBudget,
  { getCpm, typeOfUpdate = 'default' } = {},
) {
  const resolveCpm = getCpm || (display => display.smart_campaign_cpm);
  let newValue = newGroupBudget;
  let newCustomContent = [...customContents];

  if (typeOfUpdate === 'percentage') {
    const allSiblingsFixed = siblingDisplays.every(display => {
      const displayContents = newCustomContent.filter(c => c.displayId === display.id);
      return displayContents.length > 0 && displayContents.every(content => content.isFixedBudget);
    });

    if (!siblingDisplays || siblingDisplays.length === 0 || allSiblingsFixed) {
      return newCustomContent;
    }

    const minGroupBudget = calcGroupMin(groupDisplays, customContents, { getCpm: resolveCpm });
    if (newValue < minGroupBudget) newValue = minGroupBudget;

    const restOfValue = newValue - minGroupBudget;
    const maxGroupBudget = calcGroupMax(parentBudget, siblingDisplays, customContents, { getCpm: resolveCpm });

    const percentageOfTotal = (newValue / parentBudget) * 100;
    if (percentageOfTotal >= 99.9) {
      // Zero out all non-fixed sibling contents
      siblingDisplays.forEach(display => {
        const displayContents = newCustomContent.filter(c => c.displayId === display.id && !c.isFixedBudget);
        displayContents.forEach(content => {
          const idx = newCustomContent.findIndex(c => c.relationId === content.relationId);
          if (idx !== -1) {
            newCustomContent[idx] = { ...newCustomContent[idx], promised_shows: 0 };
          }
        });
      });

      const fullValue = maxGroupBudget - minGroupBudget;

      groupDisplays.forEach(display => {
        const cpm = resolveCpm(display) / 1000;
        const modifiableContents = newCustomContent.filter(c => c.displayId === display.id && !c.isFixedBudget);
        const displaysWithModifiable = groupDisplays.filter(d =>
          newCustomContent.some(c => c.displayId === d.id && !c.isFixedBudget),
        );
        if (modifiableContents.length === 0 || cpm === 0) return;

        const investmentPerContent =
          (fullValue / displaysWithModifiable.length) / modifiableContents.length;

        modifiableContents.forEach(content => {
          const promised_shows = Math.floor(cpm > 0 ? investmentPerContent / cpm : 0);
          const idx = newCustomContent.findIndex(c => c.relationId === content.relationId);
          if (idx !== -1) {
            newCustomContent[idx] = { ...newCustomContent[idx], promised_shows };
          }
        });
      });

      return newCustomContent;
    }

    // Assign restOfValue to group displays
    let groupRemainder = 0;
    groupDisplays.forEach(display => {
      const cpm = resolveCpm(display) / 1000;
      const modifiableContents = newCustomContent.filter(c => c.displayId === display.id && !c.isFixedBudget);
      const displaysWithModifiable = groupDisplays.filter(d =>
        newCustomContent.some(c => c.displayId === d.id && !c.isFixedBudget),
      );
      if (modifiableContents.length === 0 || cpm === 0) return;

      const investmentPerContent = (restOfValue / displaysWithModifiable.length) / modifiableContents.length;
      let displayRemainder = 0;

      modifiableContents.forEach(content => {
        const investmentForContent = investmentPerContent + displayRemainder;
        const rawShows = investmentForContent / cpm;
        const promised_shows = Math.floor(rawShows);
        displayRemainder = (rawShows - promised_shows) * cpm;

        const idx = newCustomContent.findIndex(c => c.relationId === content.relationId);
        if (idx !== -1) {
          newCustomContent[idx] = { ...newCustomContent[idx], promised_shows };
        }
      });
      groupRemainder += displayRemainder;
    });

    // Distribute remaining investment to sibling displays
    const remainingInvestment = parentBudget - newValue;

    const fixedSiblings = siblingDisplays.filter(display =>
      newCustomContent.some(content => content.displayId === display.id && content.isFixedBudget),
    );
    const modifiableSiblings = siblingDisplays.filter(display =>
      !fixedSiblings.some(fixed => fixed.id === display.id),
    );

    const fixedInvestment = fixedSiblings.reduce((total, display) => {
      const contents = newCustomContent.filter(c => c.displayId === display.id);
      const cpm = resolveCpm(display);
      return total + contents.reduce((sum, content) => sum + budgetFromSpots(content.promised_shows, cpm), 0);
    }, 0);

    const finalRemainingInvestment = remainingInvestment - fixedInvestment + groupRemainder;

    if (modifiableSiblings.length > 0) {
      const investmentPerSibling = finalRemainingInvestment / modifiableSiblings.length;
      let siblingRemainder = 0;

      modifiableSiblings.forEach(display => {
        const cpm = resolveCpm(display) / 1000;
        const modifiableContents = newCustomContent.filter(c => c.displayId === display.id && !c.isFixedBudget);
        if (modifiableContents.length === 0 || cpm === 0) return;

        const investmentPerContent = investmentPerSibling / modifiableContents.length;
        let displayRemainder = 0;

        modifiableContents.forEach(content => {
          const investmentForContent = investmentPerContent + displayRemainder;
          const rawShows = investmentForContent / cpm;
          const promised_shows = Math.floor(rawShows);
          displayRemainder = (rawShows - promised_shows) * cpm;

          const idx = newCustomContent.findIndex(c => c.relationId === content.relationId);
          if (idx !== -1) {
            newCustomContent[idx] = { ...newCustomContent[idx], promised_shows };
          }
        });
        siblingRemainder += displayRemainder;
      });

      if (siblingRemainder > 0) {
        const lowestCpmSibling = modifiableSiblings
          .filter(display => {
            const cpm = resolveCpm(display) / 1000;
            return cpm > 0 && newCustomContent.some(c => c.displayId === display.id && !c.isFixedBudget);
          })
          .sort((a, b) => resolveCpm(a) - resolveCpm(b))[0];

        if (lowestCpmSibling) {
          const cpm = resolveCpm(lowestCpmSibling) / 1000;
          const firstIdx = newCustomContent.findIndex(
            c => c.displayId === lowestCpmSibling.id && !c.isFixedBudget,
          );
          if (firstIdx !== -1) {
            const additionalShows = Math.floor(siblingRemainder / cpm);
            const showsToAdd = Math.max(1, additionalShows);
            newCustomContent[firstIdx] = {
              ...newCustomContent[firstIdx],
              promised_shows: newCustomContent[firstIdx].promised_shows + showsToAdd,
            };
          }
        }
      }
    }

    return newCustomContent;
  }

  // --- 'default' typeOfUpdate: assign absolute budget to the group ---

  const minGroupBudget = calcGroupMin(groupDisplays, customContents, { getCpm: resolveCpm });
  if (newValue < minGroupBudget) newValue = minGroupBudget;

  const fixedBudget = groupDisplays.reduce((total, display) => {
    const fixedContents = newCustomContent.filter(c => c.displayId === display.id && c.isFixedBudget);
    const cpm = resolveCpm(display);
    return total + fixedContents.reduce((sum, content) => sum + budgetFromSpots(content.promised_shows, cpm), 0);
  }, 0);

  const availableBudget = Math.max(0, newValue - fixedBudget);

  const displaysWithModifiable = groupDisplays.filter(display =>
    newCustomContent.some(c => c.displayId === display.id && !c.isFixedBudget),
  );

  if (displaysWithModifiable.length > 0) {
    const budgetPerDisplay = availableBudget / displaysWithModifiable.length;

    displaysWithModifiable.forEach(display => {
      const cpm = resolveCpm(display) / 1000;
      if (cpm <= 0) return;

      const modifiableContents = newCustomContent.filter(c => c.displayId === display.id && !c.isFixedBudget);
      if (modifiableContents.length === 0) return;

      const budgetPerContent = budgetPerDisplay / modifiableContents.length;

      modifiableContents.forEach(content => {
        const promised_shows = Math.floor(budgetPerContent / cpm);
        const idx = newCustomContent.findIndex(c => c.relationId === content.relationId);
        if (idx !== -1) {
          newCustomContent[idx] = { ...newCustomContent[idx], promised_shows };
        }
      });
    });
  }

  return newCustomContent;
}

/**
 *
 * @param {number}   newGroupImpressions - Total impressions for the group
 * @param {Array}    groupDisplays       - Displays in the group
 * @param {Array}    items               - customContents (group subset or full array)
 * @param {object}   [options]
 * @param {function} [options.getMultiplier] - (display) => impressions per playout;
 *                                             defaults to display.multiplier
 * @returns {Array} - Updated customContents (no side effects)
 */
// Guard against binary-float noise (e.g. 5.999999999999999 flooring to 5) when
// the carried remainder is compared against a multiplier.
const FLOOR_EPSILON = 1e-9;

function byGroupImpressions(newGroupImpressions, groupDisplays, items, { getMultiplier } = {}) {
  const resolveMultiplier = display => {
    const value = getMultiplier ? getMultiplier(display) : display && display.multiplier;
    const parsed = Number(value);
    return isFinite(parsed) && parsed > 0 ? parsed : 0;
  };

  const result = [...items];

  const fixedImpressions = groupDisplays.reduce((total, display) => {
    const multiplier = resolveMultiplier(display);
    const fixedContents = result.filter(c => c.displayId === display.id && c.isFixedBudget);
    return total + fixedContents.reduce(
      (sum, content) => sum + impressionsFromSpots(content.promised_shows, multiplier),
      0,
    );
  }, 0);

  let newValue = Number(newGroupImpressions);
  if (!isFinite(newValue) || newValue < fixedImpressions) newValue = fixedImpressions;

  const availableImpressions = Math.max(0, newValue - fixedImpressions);

  // Only displays that can actually convert impressions into playouts take part
  // in the split. Leaving a multiplier-less display in the divisor would strand
  // its share with nobody to assign it to.
  const displaysWithModifiable = groupDisplays.filter(
    display =>
      resolveMultiplier(display) > 0 &&
      result.some(c => c.displayId === display.id && !c.isFixedBudget),
  );

  if (displaysWithModifiable.length === 0) return result;

  const impressionsPerDisplay = availableImpressions / displaysWithModifiable.length;

  let carry = 0;

  displaysWithModifiable.forEach(display => {
    const multiplier = resolveMultiplier(display);
    const modifiableContents = result.filter(c => c.displayId === display.id && !c.isFixedBudget);
    const impressionsPerContent = impressionsPerDisplay / modifiableContents.length;

    modifiableContents.forEach(content => {
      const impressionsForContent = impressionsPerContent + carry;
      const promised_shows = Math.floor(impressionsForContent / multiplier + FLOOR_EPSILON);
      carry = impressionsForContent - promised_shows * multiplier;

      const idx = result.findIndex(c => c.relationId === content.relationId);
      if (idx !== -1) {
        result[idx] = { ...result[idx], promised_shows };
      }
    });
  });

  const cheapestDisplay = displaysWithModifiable.reduce((cheapest, display) =>
    resolveMultiplier(display) < resolveMultiplier(cheapest) ? display : cheapest,
  );
  const cheapestMultiplier = resolveMultiplier(cheapestDisplay);

  if (carry >= cheapestMultiplier) {
    const idx = result.findIndex(c => c.displayId === cheapestDisplay.id && !c.isFixedBudget);
    if (idx !== -1) {
      const extraShows = Math.floor(carry / cheapestMultiplier + FLOOR_EPSILON);
      result[idx] = {
        ...result[idx],
        promised_shows: result[idx].promised_shows + extraShows,
      };
    }
  }

  return result;
}

/**
 * @param {Array}    displays
 * @param {object}   [options]
 * @param {function} [options.getMultiplier] - (display) => impressions per playout
 * @returns {number} - Step in impressions (at least 1)
 */
function impressionsStep(displays, { getMultiplier } = {}) {
  if (!displays || displays.length === 0) return 1;

  const maxMultiplier = displays.reduce((max, display) => {
    const value = Number(getMultiplier ? getMultiplier(display) : display && display.multiplier);
    return isFinite(value) && value > max ? value : max;
  }, 0);

  if (maxMultiplier <= 0) return 1;
  return Math.round(maxMultiplier * displays.length) + 1;
}

// ---------------------------------------------------------------------------
// transfer — budget transfer between ad versions / displays (active campaigns)
// ---------------------------------------------------------------------------
function minimumInvestmentFloor(display, campaignDays) {
  if (!display) return 0;
  const min = display.minimum_programmatic_spots || 0;
  if (min <= 0) return 0;
  const period = display.minimum_programmatic_period || 0;
  if (period === 0) {
    if (!campaignDays || campaignDays <= 0) return 0;
    return min * campaignDays;
  }
  return min * period;
}

function displayExtractionFloor(display, appliedDiscount, days) {
  let discountFloor = 0;
  if (appliedDiscount && appliedDiscount.min_impressions && days > 0) {
    // Honor discount_type ("daily" → min * days, "total" → min)
    discountFloor = getDiscountFloorSpots(appliedDiscount, days);
  }
  const minFloor = minimumInvestmentFloor(display, days);
  return Math.max(discountFloor, minFloor);
}

/**
 * Calculate credit extractable from origin contents, respecting per-display
 * minimum spots required by volume discounts.
 *
 * Core of handleTransfer in ImpressionManagementComponent (origin side).
 *
 * @param {Array}    originContents              - Contents to extract from
 * @param {number}   percentage                  - Transfer percentage (0–100)
 * @param {object}   options
 * @param {function} options.getCpm              - (content) => full CPM
 * @param {function} options.getMinSpotsToKeep   - (displayContents[]) => min total spots to keep for the display
 * @returns {{ totalCredit: number, impressionChanges: Map<string, number> }}
 */
function calcTransferCredit(originContents, percentage, { getCpm, getMinSpotsToKeep, getValue }) {
  const pct = percentage / 100;
  const impressionChanges = new Map();
  let totalCredit = 0;

  const resolveValue = getValue || (c =>
    c.promised_shows_update !== undefined ? c.promised_shows_update : (c.promised_shows || 0)
  );

  const byDisplay = originContents.reduce((acc, c) => {
    if (!acc[c.displayId]) acc[c.displayId] = [];
    acc[c.displayId].push(c);
    return acc;
  }, {});

  Object.values(byDisplay).forEach(displayOrigins => {
    const currentTotal = displayOrigins.reduce((sum, o) => {
      return sum + resolveValue(o);
    }, 0);

    const minToKeep = getMinSpotsToKeep(displayOrigins);
    const maxExtractable = Math.max(0, currentTotal - minToKeep);
    let extracted = 0;

    displayOrigins.forEach(origin => {
      const current = resolveValue(origin);
      const desired = Math.floor(current * pct);
      const remaining = Math.max(0, maxExtractable - extracted);
      const toReduce = Math.min(desired, remaining, current);
      extracted += toReduce;

      const cpm = getCpm(origin);
      if (toReduce > 0 && cpm > 0) {
        totalCredit += budgetFromSpots(toReduce, cpm);
      }
      impressionChanges.set(origin.relationId, current - toReduce);
    });
  });

  return { totalCredit, impressionChanges };
}

/**
 * Distribute transfer credit evenly among destination contents.
 *
 * Core of handleTransfer in ImpressionManagementComponent (destination side).
 *
 * @param {number}   totalCredit          - Credit to distribute (in currency units)
 * @param {Array}    destinationContents  - Contents that receive the credit
 * @param {object}   options
 * @param {function} options.getCpm       - (content) => full CPM
 * @returns {Map<string, number>}         - relationId → new promised_shows
 */
function distributeTransferCredit(totalCredit, destinationContents, { getCpm, getValue }) {
  const impressionChanges = new Map();
  if (totalCredit <= 0 || destinationContents.length === 0) return impressionChanges;

  const resolveValue = getValue || (c =>
    c.promised_shows_update !== undefined ? c.promised_shows_update : (c.promised_shows || 0)
  );

  const creditPerDest = totalCredit / destinationContents.length;

  let remainder = 0;
  destinationContents.forEach(dest => {
    const current = resolveValue(dest);
    const cpm = getCpm(dest);
    if (cpm <= 0) {
      impressionChanges.set(dest.relationId, current);
      return;
    }
    const investment = creditPerDest + remainder;
    const { spots: toAdd, remainder: newRemainder } = spotsFromBudgetWithRemainder(investment, cpm);
    remainder = newRemainder;
    impressionChanges.set(dest.relationId, current + toAdd);
  });

  return impressionChanges;
}

/**
 * Calculate the minimum spots one record must keep so that the display total
 * (sum of all records' pending spots) does not break either the applied
 * volume discount or the display's programmatic minimum investment.
 *
 * Used by getMinSpotsForRecord in useBudgetTransfer to enforce the floor
 * on individual input fields in AdViewComponent.
 *
 * @param {object} record             - A customContent item enriched with appliedDiscount and relationShowDays
 * @param {Array}  allDisplayContents - All customContents for the same display (from contentsDisplaysSelect)
 * @param {object} [options]
 * @param {object} [options.display]      - { minimum_programmatic_spots, minimum_programmatic_period }
 * @param {number} [options.campaignDays] - Campaign length in days (fallback when record has no rules)
 * @returns {number}
 */
function minSpotsForRecord(record, allDisplayContents, options = {}) {
  if (record.revision_status === 'out_of_service' || record.revision_status === 'rejected') return 0;
  const { display, campaignDays } = options;
  const days = record.relationShowDays || campaignDays || 0;
  if (days <= 0) return 0;

  // Floor composes discount (discount_type-aware) + minimum programmatic investment
  const minForDisplay = displayExtractionFloor(display, record.appliedDiscount, days);
  if (minForDisplay <= 0) return 0;

  const otherTotal = allDisplayContents
    .filter(c => c.relationId !== record.relationId)
    .reduce((sum, c) => {
      const v = c.promised_shows_update !== undefined ? c.promised_shows_update : (c.promised_shows || 0);
      return sum + v;
    }, 0);

  return Math.max(0, minForDisplay - otherTotal);
}

/**
 * Compute how many spots were added or subtracted for a content item during a transfer session.
 *
 * @param {'add'|'subtract'} type
 * @param {object}           content - Has promised_shows_origin and promised_shows_update
 * @returns {number}
 */
function transferDiff(type, content) {
  if (content.promised_shows_update === undefined) return 0;
  const diff = type === 'add'
    ? content.promised_shows_update - content.promised_shows_origin
    : content.promised_shows_origin - content.promised_shows_update;
  return diff > 0 ? diff : 0;
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export const spots = {
  fromBudget: spotsFromBudget,
  toBudget: budgetFromSpots,
  fromBudgetWithRemainder: spotsFromBudgetWithRemainder,
};

// Conversiones de unidad para la CAPA USUARIO. Ver el bloque de comentarios de
// `impressions` más arriba: no existe `fromBudgetWithRemainder` a propósito —
// toda distribución con carry vive en `distribution.*` y opera sobre salidas.
export const impressions = {
  fromSpots: impressionsFromSpots,
  toSpots: spotsFromImpressions,
  fromBudget: impressionsFromBudget,
  toBudget: budgetFromImpressions,
  effectiveCpmi,
  getMultiplyFromPrices,
};

export const budget = {
  normalize,
  sumFixed,
  calcGroupMin,
  calcGroupMax,
};

export const distribution = {
  byBudget,
  equitably,
  byGroup,
  byGroupImpressions,
  impressionsStep,
};

export const transfer = {
  calcCredit: calcTransferCredit,
  distributeCredit: distributeTransferCredit,
  minSpotsForRecord,
  minimumInvestmentFloor,
  displayExtractionFloor,
  diff: transferDiff,
};
